import React from "react";
import {Handle, Position} from "@xyflow/react";
import {FamilyTreeNodeStyle} from "../models/FamilyTreeType";

interface FamilyTreeNodeProps {
    data: {
        firstName: string;
        middleInitial?: string;
        lastName: string;
        age?: number;
        onClick?: () => void;
        state?: string;
        relationship?: string;
        style?: FamilyTreeNodeStyle;
    };
    selected?: boolean;
}

const styleClassMap: Record<FamilyTreeNodeStyle, string> = {
    [FamilyTreeNodeStyle.PRIMARY_CONTACT]: "family-tree-node--primary-contact",
    [FamilyTreeNodeStyle.PRIMARY_LEGAL_PARTNER]: "family-tree-node--primary-legal-partner",
    [FamilyTreeNodeStyle.DECEASED]: "family-tree-node--deceased",
    [FamilyTreeNodeStyle.DOTTED]: "family-tree-node--dotted",
    [FamilyTreeNodeStyle.BUTTON]: "family-tree-node--button",
    [FamilyTreeNodeStyle.DEFAULT]: "family-tree-node--default",
};

function handleNodeKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    const {key, currentTarget} = event;

    if (key === "Enter" || key === " ") {
        event.preventDefault();
        currentTarget.click();
        return;
    }

    if (key === "Escape") {
        event.preventDefault();
        if (document.activeElement === currentTarget) {
            currentTarget.blur();
        }
    }
}

interface AgeStateProps {
    age?: number;
    state?: string;
    relationship?: string;
    showRelationship?: boolean;
}

const AgeState = React.memo(({age, state, relationship, showRelationship = false}: AgeStateProps) => {
    const hasState = !!state && state.trim() !== '';
    const hasRelationship = showRelationship && !!relationship && relationship.trim() !== '';

    if (age === undefined && !hasState && !hasRelationship) {
        return (
            <div className="family-tree-node__age family-tree-node__age--placeholder" aria-hidden="true">
                &nbsp;
            </div>
        );
    }

    return (
        <div className="family-tree-node__age">
            {age ?? null}
            {age !== undefined && hasState ? ", " : null}
            {hasState ? state : null}
            {(age !== undefined || hasState) && hasRelationship ? " " : null}
            {hasRelationship ? relationship : null}
        </div>
    );
});

const FamilyTreeNode = ({data, selected}: FamilyTreeNodeProps) => {
    const {firstName, middleInitial, lastName, age, onClick, state, relationship, style = FamilyTreeNodeStyle.DEFAULT} = data;
    const displayFirstName = middleInitial ? `${firstName} ${middleInitial}.` : firstName;
    const isDeceased = style === FamilyTreeNodeStyle.DECEASED;
    const displayAge = isDeceased ? undefined : age;
    const displayState = isDeceased ? undefined : state;
    const nodeContent = `${displayFirstName} ${lastName}${displayAge !== undefined ? ", Age: " + displayAge : ""}${displayState ? ", " + displayState : ""}${relationship ? ", " + relationship : ""}`;
    const showRelationshipInline = style === FamilyTreeNodeStyle.DOTTED;
    const isLongName = (firstName?.length > 15 || lastName?.length > 15);
    const nodeClass = [
        "family-tree-node",
        styleClassMap[style] ?? styleClassMap[FamilyTreeNodeStyle.DEFAULT],
        isLongName ? "family-tree-node--min-width-260" : "",
        onClick ? "family-tree-node--clickable" : "",
        selected ? "family-tree-node--selected" : "",
    ].filter(Boolean).join(" ");
    return (
        <div
            onClick={onClick}
            onKeyDown={onClick ? handleNodeKeyDown : undefined}
            className={nodeClass}
            title={nodeContent}
            role={onClick ? "button" : undefined}
        >
            <div className="family-tree-node__first-name">{displayFirstName}</div>
            {lastName && <div className="family-tree-node__last-name">{lastName}</div>}
            <AgeState age={displayAge} state={displayState} relationship={relationship} showRelationship={showRelationshipInline} />
            <Handle type="target" position={Position.Top} id="top" className="family-tree-node__handle" data-testid="handle-top" />
            <Handle type="source" position={Position.Bottom} id="bottom" className="family-tree-node__handle" data-testid="handle-bottom" />
            <Handle type="source" position={Position.Right} id="right" className="family-tree-node__handle" data-testid="handle-right" />
            <Handle type="target" position={Position.Left} id="left" className="family-tree-node__handle" data-testid="handle-left" />
        </div>
    );
};

const areFamilyTreeNodePropsEqual = (prev: FamilyTreeNodeProps, next: FamilyTreeNodeProps) => {
    const prevData = prev.data;
    const nextData = next.data;

    return prev.selected === next.selected
        && prevData.firstName === nextData.firstName
        && prevData.middleInitial === nextData.middleInitial
        && prevData.lastName === nextData.lastName
        && prevData.age === nextData.age
        && prevData.state === nextData.state
        && prevData.relationship === nextData.relationship
        && prevData.style === nextData.style
        && prevData.onClick === nextData.onClick;
};

export default React.memo(FamilyTreeNode, areFamilyTreeNodePropsEqual);
